/**
 * The detached-house intro line on /homes-for-sale/bend (Matt approved the
 * wording, 2026-10-23):
 *
 *   "{detachedN} of the {totalN} Bend listings here are detached single
 *    family houses. The rest are condos, townhomes, manufactured homes and
 *    land."
 *
 * {totalN} is the header claim's own figure (publishSplitClaim, settled per
 * lib/search/settled-frame.ts), passed in, never recounted, so the line and
 * the header cannot drift. {detachedN} counts the same population that figure
 * counts:
 *   - rows in hand are the whole frame (not truncated): the SFR rows among
 *     them, the very rows the figure counted;
 *   - rows cut at the display cap (truncated): the server's detached count
 *     for the same filter and bbox as the total (getViewportSearch
 *     detachedCount), only while the figure IS that total.
 * A floor, a pending count, or an unknown detached count hides the line
 * rather than estimating it.
 */
import { formatCount } from '@/lib/format/count'

/** The place the line is about: geoScopeLabel for the city alone. */
export const BEND_INTRO_CITY = 'Bend'

/** AGENTS.md: SFR = property_sub_type 'Single Family Residence'. */
export const DETACHED_SUBTYPE = 'Single Family Residence'

export type BendDetachedIntro = {
  detached: number
  total: number
  text: string
}

export function publishBendDetachedIntro(input: {
  /** The header claim, or null while it is pending or empty. */
  claim: { figure: number; figureIsFloor: boolean; truncated: boolean } | null
  /** The rows the claim's figure counts when not truncated (the cards and pins). */
  rows: ReadonlyArray<{ PropertySubType?: string | null }>
  /** The frame's total the truncated figure is read from (state totalCount). */
  frameTotal: number
  /** Detached houses in the same frame, from the same read as frameTotal; null when unknown. */
  frameDetached: number | null | undefined
  /** The view is the Bend city place, still pinned (no pan, no drawn area). */
  bendPinned: boolean
  /** A property type was chosen: "the rest" is no longer the other types. */
  typeFiltered: boolean
  /** The Sold scope counts closed sales, not listings. */
  sold: boolean
}): BendDetachedIntro | null {
  const { claim } = input
  if (!claim || !input.bendPinned || input.typeFiltered || input.sold) return null
  if (claim.figureIsFloor) return null
  const total = claim.figure
  if (!Number.isInteger(total) || total <= 0) return null
  let detached: number
  if (claim.truncated) {
    const d = input.frameDetached
    if (total !== input.frameTotal || d == null || !Number.isInteger(d) || d < 0) return null
    detached = d
  } else {
    if (input.rows.length !== total) return null
    detached = input.rows.filter((r) => r.PropertySubType === DETACHED_SUBTYPE).length
  }
  // A part never exceeds its whole, and "The rest are ..." needs a rest.
  if (detached >= total) return null
  return {
    detached,
    total,
    text: `${formatCount(detached)} of the ${formatCount(total)} Bend listings here are detached single family houses. The rest are condos, townhomes, manufactured homes and land.`,
  }
}
